import { useMemo } from 'react';

import useSubscriptionStatus from 'app/hooks/useSubscriptionStatus';
import { useIsJussubClient } from 'app/pages/PlanAndPayment/PlanUpgradeModal/hooks/useJussubPlanOffers';
import {
  usePricingCatalogOffers,
  AuthenticatedOffer,
  PricingCatalogBillingType,
  PricingCatalogErrorCode,
  PricingCatalogFeature,
} from 'app/services/pricingCatalog';

import { NewTierPlan } from './getNewTierPlans';
import { CurrentTier } from './resolveCurrentTier';
import { formatPrice } from './utils/formatPrice';
import { formatExploreSummaryFeature } from './formatExploreSummaryFeature';
import { getReactivationPromoMessage } from './reactivationPromoMessages';

export type ExploreSummaryFeature = {
  code: string;
  label: string;
  enabled: boolean;
};

export type ExplorePlanOffer = Pick<
  NewTierPlan,
  'key' | 'title' | 'price' | 'priceValue' | 'id' | 'gatewayId' | 'tagline'
> & {
  isRecommended: boolean;
  isCurrent: boolean;
  isDowngrade: boolean;
  summaryFeatures: ExploreSummaryFeature[];
  billingType: PricingCatalogBillingType;
  // Full per-feature availability, used by ComparePlansModal (6c).
  features: PricingCatalogFeature[];
  // Catálogo (id da oferta resolvida) e convênio (código do cupom), usados só no
  // tracking de upgrade. string (UUID) quando a oferta vem do jussub.
  catalogOfferId: number | string | null;
  couponCode: string | null;
  // Preço cheio ("de R$ X"), só quando `price` já é o preço com desconto do cupom.
  originalPrice: string | null;
  // Texto da oferta especial, escrito no backend (campanha muda; o front não fixa
  // frase). Vem do cupom: `subtitle` é o destaque e `description` o detalhe.
  promoMessage: PromoMessage | null;
  // Espelha `is_promo` da API: o backend decide a elegibilidade, o front só sinaliza.
  isPromo: boolean;
  // Vai no POST /subscription/create quando presente (só ofertas do jussub).
  paymentLinkCode: string | null;
};

export type PromoMessage = {
  title: string;
  description: string;
};

export type PlansCatalog = {
  currentTier: CurrentTier;
  plans: ExplorePlanOffer[];
  isLoading: boolean;
  error: PricingCatalogErrorCode | null;
  refetch: () => void;
};

export type UsePlansCatalogOptions = {
  // Paywalls internos só precisam do catálogo quando o próprio branch que
  // renderiza os tiers novos está ativo; default true preserva o comportamento dos consumidores
  // existentes (ExplorePlansModal/ComparePlansModal), que sempre precisam do catálogo.
  enabled?: boolean;
};

// Tagline from frame 6a, distinct from NEW_TIER_TAGLINES (getNewTierPlans.ts) which
// still serves PreviewExpirationModal.
const EXPLORE_TAGLINES_BY_KEY: Record<NewTierPlan['key'], string> = {
  one: 'O essencial para começar',
  ultimate_2026: 'Pra quem quer ir além do básico',
  black: 'Sem limites nas principais ferramentas',
};

// tier (API) -> key (front) — 'ultimate' (API) mapeia pra 'ultimate_2026'; 'preview'
// (Degustação) não tem card no Explorar planos.
const TIER_TO_PLAN_KEY: Partial<Record<AuthenticatedOffer['tier'], NewTierPlan['key']>> = {
  one: 'one',
  ultimate: 'ultimate_2026',
  black: 'black',
};

// cliente de convênio (OAB) em upgrade: o gateway_id cobrado é o
// do cupom da oferta quando houver, não o do plano base. Sem isso, o checkout autenticado cobraria
// o preço cheio de um cliente OAB.
const resolveOfferGatewayId = (offer: AuthenticatedOffer): number | string =>
  offer.coupon?.gateway_plan_id ?? offer.plan.gateway_id;

type CouponPricing = { amount: number; oldAmount: number };

// JusSub entrega o desconto no cupom: coupon.amount é o preço com desconto e
// coupon.old_amount o cheio (amount da raiz continua sendo o cheio). Cupom sem
// pricing (ou sem desconto de fato) vale como sem cupom.
const getCouponPricing = (offer: AuthenticatedOffer): CouponPricing | null => {
  const { amount, old_amount: oldAmount } = offer.coupon ?? {};
  if (typeof amount !== 'number' || typeof oldAmount !== 'number') return null;
  return amount < oldAmount ? { amount, oldAmount } : null;
};

const PROMO_MESSAGE_FALLBACK_TITLE = 'Oferta especial';

// O texto do backend (cupom) manda; sem ele, vale o texto fixo do plano de
// reativação do JusBill, achado pelo gateway_id cobrado. Sem nenhum dos dois, o
// selo do card basta e nenhum aviso é montado.
const getPromoMessage = (offer: AuthenticatedOffer): PromoMessage | null => {
  const subtitle = offer.coupon?.subtitle?.trim();
  const description = offer.coupon?.description?.trim();
  if (subtitle || description) {
    return {
      title: subtitle || PROMO_MESSAGE_FALLBACK_TITLE,
      description: description || '',
    };
  }

  return getReactivationPromoMessage(resolveOfferGatewayId(offer));
};

const mapAuthenticatedOfferToExploreOffer = (
  offer: AuthenticatedOffer,
  isDowngrade: boolean,
  isJussubClient: boolean,
): ExplorePlanOffer | null => {
  const key = TIER_TO_PLAN_KEY[offer.tier];
  if (!key) return null;

  // is_promo só existe nas ofertas do catálogo JusBill; no JusSub a oferta
  // especial é a que traz cupom com pricing.
  const jussubDiscount = isJussubClient ? getCouponPricing(offer) : null;
  const isPromo = offer.is_promo === true || jussubDiscount != null;
  const priceValue = jussubDiscount?.amount ?? offer.amount;

  return {
    key,
    title: offer.tier_display_name,
    price: formatPrice(priceValue, { hideCents: true }),
    originalPrice: jussubDiscount
      ? formatPrice(jussubDiscount.oldAmount, { hideCents: true })
      : null,
    priceValue,
    id: offer.plan.id,
    gatewayId: resolveOfferGatewayId(offer),
    tagline: EXPLORE_TAGLINES_BY_KEY[key],
    isRecommended: key === 'ultimate_2026',
    isCurrent: Boolean(offer.is_current),
    isDowngrade,
    billingType: offer.billing_type,
    features: offer.features,
    catalogOfferId: offer.id,
    couponCode: offer.coupon?.code ?? null,
    paymentLinkCode: offer.paymentLinkCode ?? null,
    isPromo,
    promoMessage: isPromo ? getPromoMessage(offer) : null,
    summaryFeatures: offer.features
      .filter(feature => offer.summary_feature_codes.includes(feature.code))
      .map(formatExploreSummaryFeature),
  };
};

const mapOffersToExploreOffers = (
  offers: AuthenticatedOffer[],
  isJussubClient: boolean,
): ExplorePlanOffer[] => {
  const catalogOffers = offers;

  return catalogOffers
    .map((offer) =>
      mapAuthenticatedOfferToExploreOffer(
        offer,
        isJussubClient && !offer.is_current && offer.can_subscribe === false,
        isJussubClient,
      ),
    )
    .filter((plan): plan is ExplorePlanOffer => plan !== null);
};

export const usePlansCatalog = ({ enabled = true }: UsePlansCatalogOptions = {}): PlansCatalog => {
  const { currentTier, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  const isJussubClient = useIsJussubClient();

  const { offers, isLoading: isOffersLoading, isError, errorCode, refetch } = usePricingCatalogOffers({
    enabled,
  });

  return useMemo(() => {
    const isLoading = isOffersLoading || isSubscriptionLoading;
    // An error body without `code` (500/timeout/proxy) still means the
    // catalog is incomplete; without this, isError alone fell through to a silent empty list
    // with no error and no retry (same code as errorCode from a parsed 422).
    const error = errorCode ?? (isError ? 'PRICING_CATALOG_INCOMPLETE' : null);
    const plans = isLoading || error
      ? []
      : mapOffersToExploreOffers(offers || [], isJussubClient);

    return { currentTier, plans, isLoading, error, refetch };
  }, [
    currentTier,
    isSubscriptionLoading,
    offers,
    isOffersLoading,
    isError,
    errorCode,
    refetch,
    isJussubClient,
  ]);
};
